import { useEffect, useMemo, useRef, useState } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import type { LlmEntry } from "../config";
import type { ModelResponse, StructuredResult } from "../types";
import { copyToClipboard } from "../utils/export";
import { friendlyLabel, pillifyInPlace } from "../utils/llmLabels";

export function InspectionDrawer({
  modelId,
  result,
  availableLlms,
  onClose,
}: {
  modelId: string | null;
  result: StructuredResult | null;
  availableLlms: LlmEntry[];
  onClose: () => void;
}) {
  const meta = useMemo(
    () => new Map(availableLlms.map((l) => [l.id, l])),
    [availableLlms],
  );
  const knownIds = useMemo(() => availableLlms.map((l) => l.id), [availableLlms]);
  useEffect(() => {
    if (!modelId) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [modelId, onClose]);

  if (!modelId || !result) return null;

  const rounds = result.rounds ?? [];
  const all = rounds.flatMap((r, idx) =>
    r.responses.filter((x) => x.model === modelId).map((x) => ({ ...x, round: idx + 1 })),
  );
  if (all.length === 0) return null;

  const latest = all[all.length - 1];
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} data-testid="drawer-backdrop" />
      <aside
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Inspection drawer for ${modelId}`}
        data-testid="inspection-drawer"
      >
        <div className="row" style={{ justifyContent: "space-between" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="label">Model</div>
            <div
              style={{ fontSize: 16, fontWeight: 600, wordBreak: "break-word" }}
            >
              {friendlyLabel(modelId, meta)}
            </div>
            <div
              className="mono muted"
              style={{ fontSize: 12, marginTop: 2, wordBreak: "break-all" }}
            >
              {modelId}
            </div>
          </div>
          <button
            className="btn ghost"
            onClick={onClose}
            aria-label="Close inspection drawer"
            style={{
              fontSize: 22,
              lineHeight: 1,
              padding: "4px 12px",
            }}
          >
            ×
          </button>
        </div>

        {all.map((r, i) => (
          <RoundBlock
            key={i}
            r={r}
            isFinal={i === all.length - 1 && all.length > 1}
            meta={meta}
            knownIds={knownIds}
          />
        ))}

        {all.length > 1 && <DiffNote a={all[0]} b={all[all.length - 1]} />}
      </aside>
    </>
  );
}

function RoundBlock({
  r,
  isFinal,
  meta,
  knownIds,
}: {
  r: ModelResponse & { round: number };
  isFinal: boolean;
  meta: Map<string, LlmEntry>;
  knownIds: string[];
}) {
  const [copied, setCopied] = useState(false);
  const resp = renderValue(r.response);
  const text =
    (r.reason ? `Reason: ${r.reason}\n\n` : "") +
    `Response: ${resp.text}\nConfidence: ${r.confidence_score}`;

  const responseRef = useRef<HTMLDivElement | null>(null);
  const reasonRef = useRef<HTMLDivElement | null>(null);

  const reasonHtml = useMemo(() => {
    if (!r.reason) return "";
    const html = marked.parse(r.reason, {
      gfm: true,
      breaks: true,
      async: false,
    }) as string;
    return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  }, [r.reason]);

  useEffect(() => {
    const labelFor = (id: string) => friendlyLabel(id, meta);
    if (responseRef.current) pillifyInPlace(responseRef.current, knownIds, labelFor);
    if (reasonRef.current) pillifyInPlace(reasonRef.current, knownIds, labelFor);
  }, [resp.text, reasonHtml, knownIds, meta]);

  return (
    <section className="panel scanlines" style={{ padding: 14, borderRadius: 8 }}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <span className="label">
          Round {r.round}
          {isFinal ? " · final" : ""}
        </span>
        <button
          className="btn ghost"
          onClick={async () => {
            if (await copyToClipboard(text)) {
              setCopied(true);
              setTimeout(() => setCopied(false), 1200);
            }
          }}
        >
          {copied ? "COPIED" : "COPY"}
        </button>
      </div>
      <div
        ref={responseRef}
        style={{ fontSize: 14, marginTop: 10, lineHeight: 1.55 }}
      >
        <div>
          <span className="muted">response ·</span>{" "}
          {resp.block ? (
            <ValueBlock text={resp.text} testId="response-block" />
          ) : (
            <span className="mono" data-testid="response-inline">
              {resp.text}
            </span>
          )}
        </div>
        <div>
          <span className="muted">confidence ·</span>{" "}
          {r.confidence_score === -1
            ? "abstain"
            : `${(r.confidence_score * 100).toFixed(1)}%`}
        </div>
        {r.error && (
          <div style={{ color: "var(--negative)" }}>
            <span className="muted">error ·</span> {r.error}
          </div>
        )}
      </div>
      {r.reason && (
        <div style={{ marginTop: 12 }}>
          <div className="label" style={{ marginBottom: 6 }}>
            Reasoning
          </div>
          <div
            ref={reasonRef}
            className="body-text markdown"
            data-testid="reason-block"
            style={{
              fontSize: 15,
              background: "var(--bg-panel-2)",
              padding: 14,
              borderRadius: 6,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              lineHeight: 1.65,
            }}
            dangerouslySetInnerHTML={{ __html: reasonHtml }}
          />
        </div>
      )}
    </section>
  );
}

function DiffNote({ a, b }: { a: ModelResponse; b: ModelResponse }) {
  const aVal = renderValue(a.response);
  const bVal = renderValue(b.response);
  const changedVote = aVal.text !== bVal.text;
  const dc = b.confidence_score - a.confidence_score;
  const anyBlock = aVal.block || bVal.block;

  return (
    <section className="panel" style={{ padding: 12, borderRadius: 8 }}>
      <span className="label">Deliberative diff</span>
      <div style={{ fontSize: 14, marginTop: 8, lineHeight: 1.55 }}>
        {anyBlock ? (
          <>
            <div style={{ color: "var(--text-dim)", fontSize: 12, marginBottom: 2 }}>
              response · round 1
            </div>
            <ValueBlock text={aVal.text} />
            <div
              style={{
                color: "var(--text-dim)",
                fontSize: 12,
                margin: "8px 0 2px",
              }}
            >
              response · round 2 {changedVote ? "(changed)" : "(unchanged)"}
            </div>
            <ValueBlock
              text={bVal.text}
              tone={changedVote ? "accent" : undefined}
            />
          </>
        ) : (
          <div>
            response · <span className="mono">{aVal.text}</span> →{" "}
            <span
              className="mono"
              style={{ color: changedVote ? "var(--accent)" : "var(--text-dim)" }}
            >
              {bVal.text}
            </span>
          </div>
        )}
        <div style={{ marginTop: 4 }}>
          confidence · {fmt(a.confidence_score)} →{" "}
          <span style={{ color: dc === 0 ? "var(--text-dim)" : dc > 0 ? "var(--positive)" : "var(--negative)" }}>
            {fmt(b.confidence_score)} ({dc >= 0 ? "+" : ""}
            {(dc * 100).toFixed(1)}%)
          </span>
        </div>
      </div>
    </section>
  );
}

/**
 * Preserve whitespace and JSON-stringify objects so the drawer doesn't render
 * `[object Object]` or silently swallow newlines in multi-line responses.
 */
export function renderValue(v: unknown): { text: string; block: boolean } {
  if (v === null || v === undefined) return { text: "—", block: false };
  if (typeof v === "object") {
    return { text: JSON.stringify(v, null, 2), block: true };
  }
  const s = String(v);
  return { text: s, block: s.includes("\n") || s.length > 80 };
}

function ValueBlock({
  text,
  tone,
  testId,
}: {
  text: string;
  tone?: "accent";
  testId?: string;
}) {
  return (
    <pre
      className="mono"
      data-testid={testId}
      style={{
        margin: "4px 0 0",
        fontSize: 13,
        lineHeight: 1.55,
        background: "var(--bg-panel-2)",
        border: "1px solid var(--border)",
        borderRadius: 4,
        padding: "8px 10px",
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        color: tone === "accent" ? "var(--accent)" : "var(--text)",
      }}
    >
      {text}
    </pre>
  );
}

function fmt(v: number) {
  return v === -1 ? "abstain" : `${(v * 100).toFixed(0)}%`;
}

export const _testing = { renderValue };
